import { useQuery } from '@tanstack/react-query';
import { useOrganizationId } from './useOrganizationId';
import type { TimePeriod, DashboardMetrics, TimePeriodOption } from '@/types/dashboard';
import {
  getAuctionMetrics,
  getDonationMetrics,
  getTicketMetrics,
  getTableCompetitionMetrics,
  getEventTablesMetrics,
} from '@/services/dashboardService';

export const TIME_PERIOD_OPTIONS: TimePeriodOption[] = [
  { label: 'Last 24 Hours', value: '24h', days: 1 },
  { label: 'Last 7 Days', value: '7d', days: 7 },
  { label: 'Last 30 Days', value: '30d', days: 30 },
  { label: 'Last 12 Months', value: '12m', days: 365 },
];

const getDateRange = (period: TimePeriod): { startDate: Date; endDate: Date } => {
  const endDate = new Date();
  const startDate = new Date();

  const option = TIME_PERIOD_OPTIONS.find(opt => opt.value === period);
  if (option) {
    startDate.setDate(startDate.getDate() - option.days);
  }

  return { startDate, endDate };
};

export const useDashboardMetrics = (timePeriod: TimePeriod = '30d') => {
  const { data: organizationId, isLoading: orgLoading } = useOrganizationId();

  const { startDate, endDate } = getDateRange(timePeriod);

  return useQuery({
    queryKey: ['dashboard-metrics', organizationId, timePeriod],
    queryFn: async (): Promise<DashboardMetrics> => {
      if (!organizationId) throw new Error('No organization ID');

      const [auction, eventTables, donations, tickets, tableCompetition] = await Promise.all([
        getAuctionMetrics(organizationId, startDate, endDate),
        getEventTablesMetrics(organizationId, startDate, endDate),
        getDonationMetrics(organizationId, startDate, endDate),
        getTicketMetrics(organizationId, startDate, endDate),
        getTableCompetitionMetrics(organizationId, startDate, endDate),
      ]);

      return { auction, eventTables, donations, tickets, tableCompetition };
    },
    enabled: !!organizationId && !orgLoading,
    staleTime: 60000, // 1 minute
  });
};
